import {Button, Modal} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconExclamationCircle} from '@tabler/icons-react';
import React, {useState} from 'react';
import type {ReportInstanceMembershipResponse} from 'src/api/ProactApiClient';
import {DeleteIcon, IconSize} from 'src/design/atoms/icons/CommonIcons';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {Autocomplete} from 'src/design/atoms/Autocomplete';
import {DataList} from 'src/design/organisms/DataList';
import {useListState} from 'src/design/organisms/useListState';
import {
  useCreateReportInstanceMutation,
  useDeleteReportInstanceMutation,
  useGetReportInstanceListQuery,
  useGetReportInstancePotentialReportsQuery,
} from 'src/redux/queries/reportInstance';
import {useAlert} from 'src/shared/hooks/useAlert';
import useDebounce from 'src/shared/hooks/useDebounce';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {ISelectOption} from 'src/types/Form';

const maxPageRows = 10;

export interface InstanceReportsListProps {
  instanceId: number;
  titleText?: string;
  itemsPerPage?: number;
}

export const InstanceReportsList: React.FC<InstanceReportsListProps> = ({
  instanceId,
  titleText,
  itemsPerPage = maxPageRows,
}) => {
  const {setAlert} = useAlert();

  const {
    searchTerm,
    setSearchTerm,
    currentPage,
    handlePageChange,
    start,
    length,
  } = useListState({
    identifier: `instance-reports-${instanceId}`,
    pageSize: itemsPerPage,
  });
  const [isDeletingReports, setIsDeletingReports] = useState<number[]>([]);
  const [isAddingReport, setIsAddingReport] = useState<boolean>(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ISelectOption | null>(
    null
  );

  const listParams = {
    start,
    length,
    search: debouncedSearchTerm,
    instanceId: instanceId,
  };

  const {
    data: listData,
    isLoading,
    refetch: refetchList,
  } = useGetReportInstanceListQuery(listParams);

  const indexListData = listData?.data ?? [];
  const totalRecords = listData?.totalRecords ?? 0;

  const {data: potentialReportsData, refetch: refetchPotential} =
    useGetReportInstancePotentialReportsQuery({
      start: 0,
      length: 100,
      search: debouncedSearchTerm,
      instanceId: instanceId,
    });

  const options = (potentialReportsData?.data ?? []) as ISelectOption[];

  const [createReportInstance] = useCreateReportInstanceMutation();
  const [deleteReportInstance] = useDeleteReportInstanceMutation();

  async function handleDelete(id: number) {
    try {
      setIsDeletingReports([...isDeletingReports, id]);
      await deleteReportInstance(id).unwrap();
      void refetchList();
      void refetchPotential();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsDeletingReports(isDeletingReports.filter(i => i !== id));
  }

  function handleShowAdd(): void {
    setShowCreateModal(true);
  }

  async function handleAdd() {
    try {
      if (!selectedReport?.id) {
        setAlert('Please select a report to continue.', 'error');
        return;
      }
      setIsAddingReport(true);

      await createReportInstance({
        instanceId: instanceId,
        quicksightReportId: +selectedReport.id,
      }).unwrap();

      setSelectedReport(null);
      setShowCreateModal(false);

      void refetchList();
      void refetchPotential();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsAddingReport(false);
  }

  function handleRowClick() {
    setAlert('To view a report, go to the Analytics module.', 'success');
  }

  async function handleReportChange(value: ISelectOption | null) {
    setSelectedReport(value);
  }

  return (
    <Stack gap="md">
      <PageHeader>
        <PageHeader.Title subtitle="Manage analytics report access">
          {titleText ?? 'Reports'}
        </PageHeader.Title>
        <PageHeader.Search
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search reports..."
        />
        <PageHeader.Actions>
          <PageHeader.Action variant="create" onClick={handleShowAdd}>
            Add Report
          </PageHeader.Action>
        </PageHeader.Actions>
      </PageHeader>
      <DataList
        isLoading={isLoading}
        data={indexListData}
        meta={undefined}
        currentPage={currentPage}
        totalRecords={totalRecords}
        recordsPerPage={itemsPerPage}
        onRowClick={handleRowClick}
        onPageChange={handlePageChange}
        onRowTextRender={(item: ReportInstanceMembershipResponse) =>
          item.quicksightReport?.name ?? ''
        }
        onRenderAfterRowNode={(item: ReportInstanceMembershipResponse) => (
          <Button
            leftSection={<DeleteIcon size={IconSize.md} />}
            size="sm"
            color="red"
            onClick={() => handleDelete(item.id)}
            loading={isDeletingReports.includes(item.id)}>
            Remove Report
          </Button>
        )}
        hasSearchTerm={searchTerm.trim().length > 0}
        hasCreationLogic={false}
      />

      <Modal
        opened={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add Report"
        size="lg">
        <Stack gap="md">
          <Alert icon={<IconExclamationCircle />} color="red" title="Important">
            <Text>
              This will grant access to the report in Groups. Permissions to
              view the report can be further restricted in the Groups module.
              Make sure the report contains appropriate data for this
              organization.
            </Text>
          </Alert>
          <Text size="sm">
            Choose a report to make it available for viewing.
          </Text>
          <Autocomplete
            label={'Report'}
            value={selectedReport}
            options={options}
            onSelectChangeCallback={handleReportChange}
          />
          <Divider />
          <Group justify="flex-end">
            <Button
              size="sm"
              variant="subtle"
              color="black"
              onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleAdd} loading={isAddingReport}>
              Submit
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};
